import {DatasetId} from '@src/constants/configurations.ts';
import {SampleFormat} from '@src/constants/dataCollections';
import {
  ByocBandRow,
  ByocCollectionWithLocation,
} from '@src/types/dataCollections.ts';

export interface CollectionRow {
  name: string;
  id: string;
  datasetId: DatasetId;
  templateId?: string;
  type?: string;
  created?: Date;
  collection?: ByocCollectionWithLocation;
}

export interface OpenCollectionRow extends CollectionRow {
  bands: ByocBandRow[];
  availabilityFrom: string;
  availabilityTo: string;
}

export const planetSandboxCollectionIds: string[] = [
  '65f7e4fb-a27a-4fae-8d79-06a59d7e6ede',
  '3f605f75-86c4-411a-b4ae-01c896f0e54e',
  '9ff30ab7-62c2-4d19-b5ef-83805c7e7602',
  'f3312c82-edea-42a1-8c9d-ada86ddcc857',
  'e3d2a21c-cb75-4311-86ac-024385c85b9c',
  'cc31cada-80d8-46fe-a746-43ac2f87b5da',
  '6b613b07-410a-4312-93ad-d1751fdc55de',
  '858254ae-0f29-4152-ac53-449efa00bbb0',
  '8d977093-cf9e-4351-8159-90f2522c29c1',
  'c48c018f-67a1-4827-a1e4-f3ab98690312',
  '28eef896-9632-4546-a99e-cea34d74b21e',
  'fc704520-fc81-439f-9016-5e162c32e736',
  'd09d8fd8-a3b3-49fb-9d78-47f3a5cc8ecc',
  'ca501757-cf8e-43a8-b1a4-1aa59ae22425',
  'd4a2a179-0c1a-4426-8d54-e2ac82830e83',
];

export function isPlanetSandboxCollection(id: string) {
  return planetSandboxCollectionIds.includes(id);
}

export const configurationSandboxDataTemplateId =
  'f1ff2454-067f-4d01-9f19-63f21d763817';
export const configurationPublicDataTemplateId =
  '60de79ca-16a7-4afd-bcbd-0261bf0156fa';

interface TemplateConfiguration {
  name: string;
  templateId: string;
  assetTypes: Set<string>;
}

function isSubset(subset: Set<string>, superset: Set<string>): boolean {
  for (const item of subset) {
    if (!superset.has(item)) {
      return false;
    }
  }
  return true;
}

const planetTemplateConfigurations: TemplateConfiguration[] = [
  {
    name: 'Aboveground carbon density template',
    templateId: '9629cef9-61fe-43d3-a2c2-b90f67ad26ae',
    assetTypes: new Set(['acd']),
  },
  {
    name: 'Biomass Proxy template',
    templateId: 'd8cd73ec-d5d3-4af8-afcf-d3d94168a4cf',
    assetTypes: new Set(['CB']),
  },
  {
    name: 'Canopy cover template',
    templateId: 'f49d161d-e33c-401e-9ebf-bd61e0f9129b',
    assetTypes: new Set(['cc']),
  },
  {
    name: 'Canopy height template',
    templateId: 'b1fe05a5-8d4c-46b9-ba85-33a6448f449f',
    assetTypes: new Set(['ch']),
  },
  {
    name: 'Land Surface Temperature template',
    templateId: 'e3e9b040-ad70-42cf-b28a-ae57078ee38c',
    assetTypes: new Set(['lst']),
  },
  {
    name: 'Soil Water Content template',
    templateId: '46d34eb0-27ed-4511-9f8c-f36a5b790022',
    assetTypes: new Set(['swc']),
  },
  {
    name: 'SkySat (ortho_analytic) template',
    templateId: 'a639b421-89a9-4156-b08a-063b5c55d416',
    assetTypes: new Set(['ortho_analytic']),
  },
  {
    name: 'SkySat (ortho_analytic with udm2) template',
    templateId: 'e4da76e5-7ab6-4e09-aa6b-0945b9b3fefc',
    assetTypes: new Set(['ortho_analytic', 'ortho_analytic_udm2']),
  },
  {
    name: 'SkySat (ortho_analytic_sr with udm2) template',
    templateId: '04da76e5-7ab6-4e09-aa6b-0945b9b3fefc',
    assetTypes: new Set(['ortho_analytic_sr', 'ortho_analytic_udm2']),
  },
  {
    name: 'SkySat (ortho_analytic_sr) template',
    templateId: '4639b421-89a9-4156-b08a-063b5c55d416',
    assetTypes: new Set(['ortho_analytic_sr']),
  },
  {
    name: 'SkySat (ortho_panchromatic) template',
    templateId: 'e10e3f7f-4cb1-4c2f-8137-9203b4c504e2',
    assetTypes: new Set(['ortho_panchromatic']),
  },
  {
    name: 'SkySat (ortho_pansharpened) template',
    templateId: '4639b421-89a9-4156-b08a-063b5c55d416',
    assetTypes: new Set(['ortho_analytic_sr']),
  },
  {
    name: 'SkySat (Visual) template',
    templateId: '121d4b3f-b42c-46df-944b-02b394a2b77d',
    assetTypes: new Set(['ortho_visual']),
  },
  {
    name: 'PSScene (ortho_analytic_4b with udm2) template',
    templateId: '12ef1c59-9fbf-41cd-b5ab-bbc976bc3862',
    assetTypes: new Set(['ortho_analytic_4b', 'ortho_udm2']),
  },
  {
    name: 'PSScene (ortho_analytic_4b) template',
    templateId: 'e3f8e2a1-151f-41fd-95ec-e377c08f5ee7',
    assetTypes: new Set(['ortho_analytic_4b']),
  },
  {
    name: 'PSScene (ortho_analytic_4b_sr with udm2) template',
    templateId: 'e72f1c59-9fbf-41cd-b5ab-bbc976bc3862',
    assetTypes: new Set(['ortho_analytic_4b_sr', 'ortho_udm2']),
  },
  {
    name: 'PSScene (ortho_analytic_4b_sr) template',
    templateId: '03f8e2a1-151f-41fd-95ec-e377c08f5ee7',
    assetTypes: new Set(['ortho_analytic_4b_sr']),
  },
  {
    name: 'PSScene (ortho_analytic_8b with udm2) template',
    templateId: '12ef1c59-9fbf-41cd-b5ab-bbc976bc3862',
    assetTypes: new Set(['ortho_analytic_8b', 'ortho_udm2']),
  },
  {
    name: 'PSScene (ortho_analytic_8b) template',
    templateId: 'e3f8e2a1-151f-41fd-95ec-e377c08f5ee7',
    assetTypes: new Set(['ortho_analytic_8b']),
  },
  {
    name: 'PSScene (ortho_analytic_8b_sr with udm2) template',
    templateId: 'e72f1c59-9fbf-41cd-b5ab-bbc976bc3862',
    assetTypes: new Set(['ortho_analytic_8b_sr', 'ortho_udm2']),
  },
  {
    name: 'PSScene (ortho_analytic_8b_sr) template',
    templateId: '03f8e2a1-151f-41fd-95ec-e377c08f5ee7',
    assetTypes: new Set(['ortho_analytic_8b_sr']),
  },
  {
    name: 'PSScene (Visual) template',
    templateId: '354d4b3f-b42c-46df-944b-02b394a2b77d',
    assetTypes: new Set(['ortho_visual']),
  },
];

export function getPlanetTemplateConfiguration(
  assetTypes: Set<string>,
): TemplateConfiguration | undefined {
  let selectedTemplate: TemplateConfiguration | undefined;
  planetTemplateConfigurations.forEach(templateConfiguration => {
    if (
      isSubset(templateConfiguration.assetTypes, assetTypes) &&
      (selectedTemplate === undefined ||
        templateConfiguration.assetTypes.size >
          selectedTemplate.assetTypes.size)
    ) {
      selectedTemplate = templateConfiguration;
    }
  });
  return selectedTemplate;
}

export const openCollections: OpenCollectionRow[] = [
  {
    name: 'Sentinel-1 GRD',
    id: 'sentinel-1-grd',
    templateId: '05f2f47d-b5ec-40dc-ae53-e0601de1dc0b',
    datasetId: DatasetId.S1GRD,
    availabilityFrom: '2014-10-03 - 00:35',
    availabilityTo: 'Present',
    bands: [
      {id: 'VV', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'VH', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'HV', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'HH', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'localIncidenceAngle'},
      {id: 'scatteringArea'},
      {id: 'shadowMask'},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Sentinel-2 L1C',
    id: 'sentinel-2-l1c',
    templateId: '1cd40209-3da0-47d4-aa8d-6c4833ad2850',
    datasetId: DatasetId.S2L1C,
    availabilityFrom: '2015-07-04 - 10:10',
    availabilityTo: 'Present',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B08', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B8A', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B09', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B10', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B11', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B12', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'CLP', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'CLM', sampleFormat: SampleFormat.UINT, bitDepth: 8, noData: 255},
      {
        id: 'sunAzimuthAngles',
        sampleFormat: SampleFormat.FLOAT,
        bitDepth: 32,
      },
      {id: 'viewAzimuthMean', sampleFormat: SampleFormat.FLOAT, bitDepth: 32},
      {id: 'sunZenithAngles', sampleFormat: SampleFormat.FLOAT, bitDepth: 32},
      {id: 'viewZenithMean', sampleFormat: SampleFormat.FLOAT, bitDepth: 32},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Sentinel-2 L2A',
    id: 'sentinel-2-l2a',
    templateId: '269dbe6e-2de2-4147-ad2d-57100fb27951',
    datasetId: DatasetId.S2L2A,
    availabilityFrom: '2015-07-04 - 10:10',
    availabilityTo: 'Present',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B08', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B8A', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B09', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B11', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'B12', sampleFormat: SampleFormat.UINT, bitDepth: 15},
      {id: 'AOT', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'SCL', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'SNW', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'CLD', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'CLP', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'CLM', sampleFormat: SampleFormat.UINT, bitDepth: 8, noData: 255},
      {
        id: 'sunAzimuthAngles',
        sampleFormat: SampleFormat.FLOAT,
        bitDepth: 32,
      },
      {id: 'viewAzimuthMean', sampleFormat: SampleFormat.FLOAT, bitDepth: 32},
      {id: 'sunZenithAngles', sampleFormat: SampleFormat.FLOAT, bitDepth: 32},
      {id: 'viewZenithMean', sampleFormat: SampleFormat.FLOAT, bitDepth: 32},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Landsat 4-5 TM L1',
    id: 'landsat-tm-l1',
    templateId: '83ce0124-ad91-462f-bd59-3d1f01fbd800',
    datasetId: DatasetId.LTML1,
    availabilityFrom: '1982-08-22 - 14:18',
    availabilityTo: '2012-05-05 - 17:54',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'BQA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'QA_RADSAT', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'VAA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'VZA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SAA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SZA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Landsat 4-5 TM L2',
    id: 'landsat-tm-l2',
    templateId: '01d9783d-fd09-437b-baee-d8f6d077faa0',
    datasetId: DatasetId.LTML2,
    availabilityFrom: '1982-08-22 - 14:18',
    availabilityTo: '2012-05-05 - 17:54',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'BQA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'QA_RADSAT', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'ST_QA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_TRAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_URAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_DRAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_ATRAN', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_EMIS', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_EMSD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_CDIST', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SR_ATMOS_OPACITY', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Landsat 7 ETM L1',
    id: 'landsat-etm-l1',
    templateId: '9cc3c65c-a9be-4d73-94d3-96c9738da9a4',
    datasetId: DatasetId.LETML1,
    availabilityFrom: '1999-05-28 - 01:02',
    availabilityTo: '2024-01-19 - 01:39',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B06_VCID_1', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B06_VCID_2', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'BQA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'QA_RADSAT', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'VAA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'VZA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SAA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SZA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Landsat 7 ETM L2',
    id: 'landsat-etm-l2',
    templateId: '69c136ec-6b58-4e7e-aa88-49bb7219275d',
    datasetId: DatasetId.LETML2,
    availabilityFrom: '1999-05-28 - 01:02',
    availabilityTo: '2024-01-19 - 01:39',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'BQA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'QA_RADSAT', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'ST_QA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_TRAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_URAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_DRAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_ATRAN', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_EMIS', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_EMSD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_CDIST', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SR_ATMOS_OPACITY', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SR_CLOUD_QA', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Landsat 8-9 OLI-TIRS L1',
    id: 'landsat-ot-l1',
    templateId: '89cbd8cc-df43-40ea-8480-4aa84fb72fdd',
    datasetId: DatasetId.LOTL1,
    availabilityFrom: '2013-03-08 - 03:25',
    availabilityTo: 'Present',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B08', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B8A', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B09', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B10', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B11', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'BQA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'QA_RADSAT', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'VAA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'VZA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SAA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SZA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Landsat 8-9 OLI-TIRS L2',
    id: 'landsat-ot-l2',
    templateId: 'dd5b8e2f-ae24-4116-8422-95020ea3da1f',
    datasetId: DatasetId.LOTL2,
    availabilityFrom: '2013-03-08 - 03:25',
    availabilityTo: 'Present',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B10', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'BQA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'QA_RADSAT', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'SR_QA_AEROSOL', sampleFormat: SampleFormat.INT, bitDepth: 8},
      {id: 'ST_QA', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_TRAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_URAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_DRAD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_ATRAN', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_EMIS', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_EMSD', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ST_CDIST', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'Harmonized Landsat Sentinel',
    id: 'hls',
    templateId: '196f2b55-5533-440f-9c48-3f229c6db291',
    datasetId: DatasetId.HLS,
    availabilityFrom: '2013-04-11 - 00:14',
    availabilityTo: 'Present',
    bands: [
      {id: 'CoastalAerosol', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'Blue', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'Green', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'Red', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'RedEdge1', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'RedEdge2', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'RedEdge3', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'NIR_Broad', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'NIR_Narrow', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SWIR1', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'SWIR2', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'WaterVapor', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'Cirrus', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ThermalInfrared1', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'ThermalInfrared2', sampleFormat: SampleFormat.INT, bitDepth: 16},
      {id: 'QA', sampleFormat: SampleFormat.UINT, bitDepth: 8},
      {id: 'VAA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'VZA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'SAA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'SZA', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'MODIS',
    id: 'modis',
    templateId: '47e32d26-ed1c-4d23-bc90-42b627644b0f',
    datasetId: DatasetId.MODIS,
    availabilityFrom: '2000-03-10 - 12:00',
    availabilityTo: '2019-12-03 - 12:00',
    bands: [
      {id: 'B01', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B02', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B03', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B04', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B05', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B06', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'B07', sampleFormat: SampleFormat.UINT, bitDepth: 16},
      {id: 'dataMask', noData: 1},
    ],
  },
  {
    name: 'DEM',
    id: 'dem',
    templateId: '0e9d246f-d73b-4747-83a5-9cbb909536e1',
    datasetId: DatasetId.DEM,
    availabilityFrom: '',
    availabilityTo: '',
    bands: [
      {id: 'DEM', sampleFormat: SampleFormat.FLOAT, bitDepth: 32},
      {id: 'dataMask', noData: 1},
    ],
  },
];

export function isOpenCollection(collectionId: string) {
  return openCollections.some(collection => collection.id === collectionId);
}

export function getOpenCollection(collectionId: string) {
  return openCollections.find(collection => collection.id === collectionId);
}
